<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import wootModal from 'dashboard/components/Modal.vue';
import wootModalHeader from 'dashboard/components/ModalHeader.vue';

const showModal = ref(false);
const isLoading = ref(false);
const conversationMessages = ref([]);
const store = useStore();
const { t } = useI18n();

const openModal = async conversationId => {
  showModal.value = true;
  isLoading.value = true;
  conversationMessages.value = [];

  try {
    const accountId = store.getters.getCurrentAccountId;
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId}/conversations/${conversationId}/messages`
    );
    conversationMessages.value = response.data.payload || [];
  } catch (error) {
    useAlert(t('CONVERSATION.LOAD_ORIGINAL_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  emitter.on('openConversationModal', openModal);
});

onUnmounted(() => {
  emitter.off('openConversationModal', openModal);
});
</script>

<template>
  <woot-modal
    v-model:show="showModal"
    :on-close="() => (showModal = false)"
    class="!max-w-2xl"
  >
    <woot-modal-header :header-title="t('CONVERSATION.ORIGINAL_TITLE')" />
    <div class="p-6 max-h-[70vh] overflow-y-auto bg-slate-50 dark:bg-slate-900">
      <div v-if="isLoading" class="flex justify-center p-4">
        <span class="text-slate-500 text-sm font-medium">{{
          t('CONVERSATION.LOADING_ORIGINAL')
        }}</span>
      </div>
      <div v-else class="space-y-4">
        <div
          v-for="msg in conversationMessages"
          :key="msg.id"
          class="flex flex-col"
          :class="
            msg.message_type === 0
              ? 'items-start'
              : msg.message_type === 1
                ? 'items-end'
                : 'items-center'
          "
        >
          <!-- Mensagem de Sistema / Atividade -->
          <div
            v-if="msg.message_type === 2"
            class="text-[11px] text-slate-500 my-2 text-center bg-slate-200/50 dark:bg-slate-800/50 px-3 py-1 rounded-full w-fit"
          >
            {{ msg.content }}
          </div>

          <!-- Mensagem Normal -->
          <div
            v-else
            class="max-w-[85%] rounded-2xl px-4 py-2 shadow-sm"
            :class="
              msg.message_type === 1
                ? 'bg-blue-600 text-white rounded-br-none'
                : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-none border border-slate-200 dark:border-slate-700'
            "
          >
            <p
              v-if="msg.content"
              class="text-[13px] whitespace-pre-wrap leading-relaxed"
            >
              {{ msg.content }}
            </p>

            <!-- Anexos -->
            <div
              v-if="msg.attachments && msg.attachments.length"
              class="mt-2 space-y-2"
            >
              <div v-for="att in msg.attachments" :key="att.id">
                <img
                  v-if="att.file_type === 'image'"
                  :src="att.data_url"
                  class="max-w-full rounded-lg shadow-sm"
                />
                <a
                  v-else
                  :href="att.data_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs underline flex items-center gap-1 hover:opacity-80"
                  :class="
                    msg.message_type === 1 ? 'text-white' : 'text-woot-500'
                  "
                >
                  <fluent-icon icon="document" size="12" />
                  {{ t('CONVERSATION.VIEW_ATTACHMENT') }}
                </a>
              </div>
            </div>
          </div>
          <span class="text-[10px] text-slate-400 mt-1 opacity-70">
            {{ new Date(msg.created_at * 1000).toLocaleString() }}
          </span>
        </div>

        <div
          v-if="conversationMessages.length === 0"
          class="text-center py-8 text-slate-500"
        >
          {{ t('CONVERSATION.NO_MESSAGES_FOUND') }}
        </div>
      </div>
    </div>
  </woot-modal>
</template>
